// An agent's results on lines become the user's notes (docs/design.md
// "Notes", Agents' results; docs/remote-relay.md "Review tasks"). This
// browser pages through each pairing's finished range tasks (relay.ts
// `results`, from the cursor it keeps per pairing) and keeps each result
// as a note by that agent (`author: agent`, the pairing's name as its
// label), whether or not any panel shows the task.
//
// The agent's computer is not trusted with where a note goes: a result is
// kept only for a task this browser sent that pairing (relay.ts
// `sentRanges`, recorded as it was sent, with the same topic), on the
// subject and lines it was sent with, and only while that subject is still
// granted to the pairing with `files`. Anything else is refused for good.
// A subject task has no lines: its answer stays in the agent panel.
//
// Once per result: the server keeps one note per task (`source_task`),
// whichever tab or browser keeps it first, and remembers a deleted one; and
// the cursor moves past each result kept or refused, so a reload asks
// nothing again. A keep that fails stops the pass there, to go on from that
// result at the next one.
import type { RelayResult, SentRange } from '../api/relay';
import { parseSubject, type SubjectKey } from '../api/relaySubject';
import type { Note, ResultNote } from '../api/server';

/** Longest agent label the server takes, in bytes (`notes::MAX_LABEL`). */
const MAX_LABEL = 64;
/** Longest note body the server takes, in bytes (`MAX_BODY`). */
const MAX_BODY = 65_536;
/** Results asked for a page at a time (the helper's most). */
export const RESULTS_PAGE = 50;

/** An agent's name as a note's label: printable, trimmed, at most 64 bytes; "Your agent" when nothing is left. */
export function agentLabel(name: string): string {
  let out = '';
  for (const ch of name.replace(/\p{Cc}/gu, ' ').replace(/\s+/g, ' ').trim()) {
    if (new TextEncoder().encode(out + ch).length > MAX_LABEL) break;
    out += ch;
  }
  return out.trim() || 'Your agent';
}

/** Text cut to at most `max` bytes of UTF-8, on a character boundary. */
function cut(text: string, max: number): string {
  const bytes = new TextEncoder().encode(text);
  if (bytes.length <= max) return text;
  return new TextDecoder().decode(bytes.slice(0, max)).replace(/�$/, '');
}

/** The note `result` is kept as, at the lines `sent` names, with its repository; null for an empty result. */
export function resultNote(result: RelayResult, sent: SentRange, agent: string): { owner: string; repo: string; note: ResultNote } | null {
  const body = cut(result.result.replace(/\0/g, ''), MAX_BODY);
  const subject = parseSubject(sent.subject);
  if (!body.trim() || !subject) return null;
  return {
    owner: subject.owner,
    repo: subject.repo,
    note: {
      path: sent.path,
      side: sent.side,
      blob: sent.blob,
      start_line: sent.start_line,
      end_line: sent.end_line,
      commit_sha: sent.commit,
      body,
      author: { kind: 'agent', label: agentLabel(agent) },
      source_task: sent.task,
    },
  };
}

/** What keeping a pairing's results takes. */
export interface SyncDeps {
  /** A page of the pairing's results after `after`. */
  results(connection: string, after: string | null, limit: number): Promise<{ results: RelayResult[]; after: string | null; more: boolean }>;
  /** The pairing's cursor, and moving it on. */
  cursor(connection: string): Promise<string | null>;
  advance(connection: string, after: string): Promise<void>;
  /** The range tasks this browser sent the pairing. */
  sent(connection: string): Promise<SentRange[]>;
  /** Whether `subject` is granted to the pairing now, with `files` (what a range task carries). */
  granted(connection: string, subject: SubjectKey): boolean;
  /** Keep `note` in `owner/repo` (the repository's notes store's `keep`, so an open page shows it): null when its note was deleted. */
  keep(owner: string, repo: string, note: ResultNote): Promise<Note | null>;
}

/** The cursor naming `r`: the helper's `<updated>:<id>`. */
const cursorOf = (r: RelayResult) => `${r.updated}:${r.id}`;

/**
 * Keep every result pairing `connection` (its agent named `agent`) has
 * finished since its cursor, a page at a time, moving the cursor past each
 * result kept or refused. Returns how many notes it kept. Throws when a
 * page can't be read; a keep that fails ends the pass where it failed.
 */
export async function syncResults(connection: string, agent: string, deps: SyncDeps): Promise<number> {
  let after = await deps.cursor(connection);
  const sent = new Map((await deps.sent(connection)).map((r) => [r.task, r]));
  let kept = 0;
  for (;;) {
    const page = await deps.results(connection, after, RESULTS_PAGE);
    for (const r of page.results) {
      const mine = sent.get(r.id);
      const note = mine && mine.topic === r.topic && deps.granted(connection, mine.subject) ? resultNote(r, mine, agent) : null;
      if (note) {
        try {
          if (await deps.keep(note.owner, note.repo, note.note)) kept++;
        } catch {
          return kept;
        }
      }
      after = cursorOf(r);
      await deps.advance(connection, after);
    }
    if (!page.more || !page.results.length) return kept;
  }
}
